import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  ArrowLeft, FileText, Shield, Bot, Loader2, Home,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EssaySubmitForm, SubmissionHistoryList } from '@/components/essayGrader/EssaySubmitForm';
import {
  ResultSummary, RubricResultPanel, PlagiarismResultPanel, AIDetectionPanel,
} from '@/components/essayGrader/ResultPanels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EssayGrader() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [activeResult, setActiveResult] = useState(null);

  // Form state
  const [studentName, setStudentName] = useState('');
  const [essayText, setEssayText] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);

  const fetchUnit = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/units/${unitId}`);
      setUnit(res.data);
    } catch (err) {
      console.warn('EssayGrader: failed to load unit', err);
      toast.error('Failed to load unit');
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/essay/submissions/${unitId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmissions(res.data);
    } catch (err) {
      // Submissions list is non-critical secondary data
      console.warn('EssayGrader: failed to load submissions', err);
    }
  }, [unitId, token]);

  useEffect(() => {
    fetchUnit();
    fetchSubmissions();
  }, [fetchUnit, fetchSubmissions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!studentName.trim() || !essayText.trim()) {
      toast.error('Please enter student name and essay text');
      return;
    }
    if (essayText.trim().split(/\s+/).length < 20) {
      toast.error('Essay must be at least 20 words');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/essay/submit?unit_id=${unitId}`,
        { student_name: studentName, essay_prompt_index: promptIndex, essay_text: essayText },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Essay graded successfully!');
      setActiveResult(res.data);
      setStudentName('');
      setEssayText('');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grade essay');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/essay/submission/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Submission deleted');
      if (activeResult?.id === id) setActiveResult(null);
      fetchSubmissions();
    } catch (err) {
      console.error('EssayGrader: delete failed', err);
      toast.error('Failed to delete');
    }
  };

  const viewResult = async (id) => {
    try {
      const res = await axios.get(`${API}/essay/submission/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveResult(res.data);
    } catch (err) {
      console.error('EssayGrader: view result failed', err);
      toast.error('Failed to load result');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-archive-paper flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-stone-600" />
      </div>
    );
  }

  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen bg-archive-paper flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <Shield className="h-12 w-12 mx-auto text-stone-400 mb-4" />
          <h2 className="text-xl font-semibold text-stone-800 mb-2">Teacher Access Required</h2>
          <p className="text-stone-600 mb-4">Sign in with a teacher account to use the Essay Grader.</p>
          <Button onClick={() => navigate('/login')} data-testid="login-redirect-btn">Sign In</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-archive-paper">
      <nav className="glass-nav sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="home-nav-btn" className="flex-shrink-0">
              <Home className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/unit/${unitId}`)} data-testid="back-to-unit-btn" className="flex-shrink-0">
              <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Unit</span>
              <span className="sm:hidden">Unit</span>
            </Button>
          </div>
          <h1 className="hidden sm:block text-lg font-semibold text-stone-800 truncate">AI Essay Grader</h1>
          <h1 className="sm:hidden text-sm font-semibold text-stone-800 truncate">Essay Grader</h1>
          <div className="w-8 sm:w-24 flex-shrink-0" />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
              Grade {unit?.grade_level}
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-100 px-2 py-0.5 rounded">
              Unit {unit?.unit_number}
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-stone-900 break-words" data-testid="unit-title">{unit?.title}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* LEFT: Submit Form + History */}
          <div className="lg:col-span-1 space-y-6">
            <EssaySubmitForm
              unit={unit}
              studentName={studentName} setStudentName={setStudentName}
              essayText={essayText} setEssayText={setEssayText}
              promptIndex={promptIndex} setPromptIndex={setPromptIndex}
              submitting={submitting}
              onSubmit={handleSubmit}
            />
            <SubmissionHistoryList
              submissions={submissions}
              activeId={activeResult?.id}
              onView={viewResult}
              onDelete={handleDelete}
            />
          </div>

          {/* RIGHT: Results */}
          <div className="lg:col-span-2 space-y-5">
            {submitting && (
              <Card className="p-12 text-center border border-stone-200 bg-white" data-testid="grading-loader">
                <Loader2 className="h-10 w-10 animate-spin text-amber-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-800">Analyzing Essay</h3>
                <p className="text-stone-500 mt-1 text-sm">Running rubric grading, plagiarism check, and AI detection simultaneously...</p>
                <div className="mt-6 max-w-xs mx-auto space-y-3">
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <FileText className="h-4 w-4 text-amber-600 animate-pulse" /> Rubric-based grading
                  </div>
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <Shield className="h-4 w-4 text-amber-600 animate-pulse" /> Plagiarism analysis
                  </div>
                  <div className="flex items-center gap-3 text-sm text-stone-600">
                    <Bot className="h-4 w-4 text-amber-600 animate-pulse" /> AI content detection
                  </div>
                </div>
              </Card>
            )}

            {!submitting && !activeResult && (
              <Card className="p-12 text-center border border-stone-200 bg-white" data-testid="empty-state">
                <FileText className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-700">No Essay Selected</h3>
                <p className="text-stone-500 mt-1 text-sm">Submit an essay on the left, or click a previous submission to view results.</p>
              </Card>
            )}

            {!submitting && activeResult && activeResult.status === 'completed' && (
              <>
                <ResultSummary result={activeResult} />
                <RubricResultPanel gr={activeResult.grade_result || {}} />
                <PlagiarismResultPanel pl={activeResult.plagiarism_result || {}} />
                <AIDetectionPanel ai={activeResult.ai_detection_result || {}} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
